import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getOnHoverScrollbarStyle } from '../../styles';
import {
  normalizeDbDateString,
  isoDateToDisplayString,
} from '../../utils/date.utils';
import { WbMoreOptions } from '../WbMoreOptions';
import { WbNoData } from '../WbNoData';
import { WbSearch } from '../WbSearch';
import {
  ChatContextProvider,
  ChatContextType,
  useChatContext,
} from './ChatContext';
import { Message } from './Messages';
import { IncomingMessage } from './Messages/IncomingMessage';
import { TextBox } from './TextBox';
import { WittyEmptyChatIcon } from './icons/WittyEmptyChatIcon';
import { WittyIcon } from './icons/WittyIcon';
import {
  WittyExpandIcon,
  WittyHistoryIcon,
  WittyNewSessionIcon,
} from './icons/icons';

export type WittySessionSummary = {
  session_id: string;
  title: string;
  created_at: string | null;
  updated_at: string | null;
};

type ChatProps = Partial<ChatContextType> & {
  setOpen: Dispatch<SetStateAction<boolean>>;
  setChatExpanded: Function;
  isMicrophoneActive?: boolean;
  isAttachContentActive?: boolean;
  isFeedbackActive?: boolean;
  onNewSession: Function;
  mobile?: boolean;
  historyOpen?: boolean;
  setHistoryOpen?: Dispatch<SetStateAction<boolean>>;
  sessions?: WittySessionSummary[];
  sessionsLoading?: boolean;
  loadSessions?: () => void;
  loadMoreSessions?: () => void;
  hasMoreSessions?: boolean;
  searchSessions?: (query: string) => void;
  sessionSearchQuery?: string;
  onSelectSession?: (sessionId: string) => void;
  onRenameSession?: (sessionId: string, newTitle: string) => Promise<void>;
  onDeleteSession?: (sessionId: string) => Promise<void>;
  activeSessionId?: string | null;
};

const useStyles = makeStyles(
  theme => ({
    root: {
      height: '100%',
      width: 'inherit',
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
    },
    boxChat: {
      paddingInline: theme.spacing(2),
      flexGrow: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      ...getOnHoverScrollbarStyle(theme, false),
    },
    wittyHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      height: '41px',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
    rightActions: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    fillMessageContainerTop: {
      flex: '1 0 0px',
    },
    textboxWrapper: {
      width: '100%',
      paddingBottom: theme.spacing(2),
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
    courtesyMessageWrapper: {
      paddingTop: theme.spacing(10),
      alignSelf: 'center',
      textAlign: 'center',
      width: '100%',
    },
    headerIconButton: {
      padding: '0',
      marginLeft: theme.spacing(1),
    },
    rootWithHistory: {
      display: 'flex',
      height: '100%',
      width: 'inherit',
    },
    chatPanel: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
      backgroundColor: theme.palette.background.default,
    },
    historySidebar: {
      width: '280px',
      minWidth: '200px',
      maxWidth: '50%',
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    historyHeader: {
      padding: theme.spacing(0, 1.5),
      height: '41px',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    historyList: {
      flexGrow: 1,
      overflowY: 'auto',
      ...getOnHoverScrollbarStyle(theme, false),
    },
    historyItem: {
      padding: theme.spacing(1, 1.5),
      cursor: 'pointer',
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
      display: 'flex',
      alignItems: 'flex-start',
      textAlign: 'left',
      width: '100%',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&:hover $historyItemMenu': {
        opacity: 1,
      },
      '&:focus-within $historyItemMenu': {
        opacity: 1,
      },
    },
    historyItemContent: {
      flex: 1,
      minWidth: 0,
    },
    historyItemMenu: {
      opacity: 0,
      flexShrink: 0,
      padding: 2,
      marginTop: -2,
      marginRight: -4,
      '&:focus-visible': {
        opacity: 1,
      },
    },
    historyItemActive: {
      backgroundColor: theme.palette.action.selected,
    },
    historyItemDate: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    historyItemTitle: {
      fontSize: '0.85rem',
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    loadMoreButton: {
      padding: theme.spacing(1),
      textAlign: 'center',
    },
  }),
  { name: 'witty-chat' },
);

const WittyHeader = ({
  setOpen,
  setChatExpanded,
  onNewSession,
  mobile = false,
  onToggleHistory,
}: {
  setOpen: Dispatch<SetStateAction<boolean>>;
  setChatExpanded: Function;
  onNewSession: Function;
  mobile?: boolean;
  onToggleHistory?: () => void;
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const { incomingMessage } = useChatContext();

  return (
    <Box className={classes.wittyHeader}>
      <WittyIcon />
      <Box className={classes.rightActions}>
        <IconButton
          className={classes.headerIconButton}
          title="New session"
          aria-label="new session"
          onClick={() => onNewSession()}
          color="primary"
          disabled={incomingMessage}
        >
          <WittyNewSessionIcon />
        </IconButton>
        {onToggleHistory && (
          <IconButton
            className={classes.headerIconButton}
            title="Conversations history"
            aria-label="conversations history"
            onClick={onToggleHistory}
            color="primary"
            disabled={incomingMessage}
          >
            <WittyHistoryIcon />
          </IconButton>
        )}
        {!mobile && (
          <IconButton
            className={classes.headerIconButton}
            title="Expand chat"
            aria-label="expand chat"
            onClick={() => setChatExpanded()}
            color="primary"
            disabled={incomingMessage}
          >
            <WittyExpandIcon />
          </IconButton>
        )}
        <div
          style={{
            borderLeft: `1px solid ${theme.palette.grey[200]}`,
            paddingLeft: theme.spacing(1),
          }}
        >
          <IconButton
            aria-label="clear"
            onClick={() => setOpen(false)}
            size="small"
            color="primary"
            disabled={incomingMessage}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
    </Box>
  );
};

type WittyContentProps = {
  isMicrophoneActive?: boolean;
  isAttachContentActive?: boolean;
  isFeedbackActive?: boolean;
};

const SCROLL_THRESHOLD_PX = 100;

const WittyContent: FC<WittyContentProps> = ({
  isMicrophoneActive,
  isAttachContentActive,
  isFeedbackActive,
}) => {
  const classes = useStyles();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { messages, incomingMessage, statusText } = useChatContext();
  const isUserScrolledUpRef = useRef(false);
  const prevIncomingMessageRef = useRef<boolean | undefined>(false);

  const scrollToBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  };

  const handleScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isUserScrolledUpRef.current = distanceFromBottom > SCROLL_THRESHOLD_PX;
  };

  // When streaming starts (incomingMessage flips to true), always scroll to
  // bottom and clear any prior user-scroll flag so the first batch lands visible.
  useEffect(() => {
    if (incomingMessage && !prevIncomingMessageRef.current) {
      isUserScrolledUpRef.current = false;
      scrollToBottom();
    }
    prevIncomingMessageRef.current = incomingMessage;
  }, [incomingMessage]);

  // For every message update (including streaming batches), only scroll to
  // bottom if the user has not manually scrolled up.
  useEffect(() => {
    if (!isUserScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  return (
    <>
      <div
        className={classes.boxChat}
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        {messages?.length === 0 && (
          <div className={classes.courtesyMessageWrapper}>
            <h2>How can I help you, today?</h2>
            <Box
              style={{
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <WittyEmptyChatIcon />
            </Box>
            <Typography variant="body1" color="textSecondary">
              Ask me anything. Make your request and I will try to answer you as
              best as I can.
            </Typography>
          </div>
        )}
        <Box className={classes.fillMessageContainerTop} />
        {messages?.map((m, idx) => (
          <Message
            sender={m.sender}
            contentMessage={m.contentMessage}
            isFeedbackActive={isFeedbackActive}
            customContent={m.customContent}
            showHeader={idx === 0 || messages[idx - 1].sender !== m.sender}
          />
        ))}
        {incomingMessage && <IncomingMessage statusText={statusText} />}
      </div>
      <Box className={classes.textboxWrapper}>
        <TextBox
          isMicrophoneActive={isMicrophoneActive}
          isAttachContentActive={isAttachContentActive}
        />
      </Box>
    </>
  );
};

const formatSessionDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  try {
    const normalized = normalizeDbDateString(dateStr);
    return isoDateToDisplayString(normalized, { showSecond: false });
  } catch {
    return dateStr;
  }
};

const HistorySidebar: FC<{
  sessions: WittySessionSummary[];
  loading: boolean;
  onSelect?: (sessionId: string) => void;
  onRename?: (sessionId: string, newTitle: string) => Promise<void>;
  onDelete?: (sessionId: string) => Promise<void>;
  onLoadMore?: () => void;
  hasMore: boolean;
  onClose?: () => void;
  activeSessionId?: string | null;
  searchQuery?: string;
  onSearch?: (query: string) => void;
}> = ({
  sessions,
  loading,
  onSelect,
  onRename,
  onDelete,
  onLoadMore,
  hasMore,
  onClose,
  activeSessionId,
  searchQuery = '',
  onSearch,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameSubmittedRef = useRef(false);

  const handleMenuClose = () => {
    setMenuSessionId(null);
  };

  const handleRenameStart = () => {
    const title =
      sessions.find(s => s.session_id === menuSessionId)?.title || '';
    handleMenuClose();
    // Delay so the Menu unmounts before the TextField mounts with autoFocus
    setTimeout(() => {
      setRenamingId(menuSessionId);
      setRenameValue(title);
      renameSubmittedRef.current = false;
    }, 150);
  };

  const handleRenameSubmit = async () => {
    if (renameSubmittedRef.current) return;
    renameSubmittedRef.current = true;
    if (renamingId && renameValue.trim() && onRename) {
      await onRename(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleRenameCancel = () => {
    renameSubmittedRef.current = true;
    setRenamingId(null);
    setRenameValue('');
  };

  const handleDeleteClick = async () => {
    if (menuSessionId && onDelete) {
      await onDelete(menuSessionId);
    }
    handleMenuClose();
  };

  return (
    <Box className={classes.historySidebar}>
      <Box className={classes.historyHeader}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, color: theme.palette.primary.main }}
        >
          Conversations history
        </Typography>
        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            color="primary"
            aria-label="Close history"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {onSearch && (
        <Box style={{ padding: '8px 12px 4px' }}>
          <WbSearch
            placeholder="Search by title..."
            value={searchQuery}
            onChange={onSearch}
            onClear={() => onSearch('')}
            label="Search conversations"
          />
        </Box>
      )}
      <Box className={classes.historyList}>
        {sessions.map(s => (
          <ButtonBase
            key={s.session_id}
            className={`${classes.historyItem}${
              s.session_id === activeSessionId
                ? ` ${classes.historyItemActive}`
                : ''
            }`}
            onClick={() => onSelect?.(s.session_id)}
            component="div"
            aria-label={`Open conversation: ${s.title || 'Untitled'}`}
          >
            <Box className={classes.historyItemContent}>
              <Typography className={classes.historyItemDate}>
                {formatSessionDate(s.updated_at ?? s.created_at)}
              </Typography>
              {renamingId === s.session_id ? (
                <TextField
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  fullWidth
                  size="small"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onBlur={handleRenameSubmit}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleRenameSubmit();
                    if (e.key === 'Escape') handleRenameCancel();
                  }}
                  onClick={e => e.stopPropagation()}
                  inputProps={{
                    style: { fontSize: '0.85rem', padding: '2px 4px' },
                  }}
                />
              ) : (
                <Typography className={classes.historyItemTitle}>
                  {s.title || 'Untitled'}
                </Typography>
              )}
            </Box>
            {(onRename || onDelete) && renamingId !== s.session_id && (
              <WbMoreOptions
                className={classes.historyItemMenu}
                open={menuSessionId === s.session_id}
                onClick={e => {
                  e.stopPropagation();
                  setMenuSessionId(
                    menuSessionId === s.session_id ? null : s.session_id,
                  );
                }}
                onClose={handleMenuClose}
                IconProps={{ style: { fontSize: 16 } }}
              >
                {onRename && (
                  <MenuItem onClick={handleRenameStart}>Rename</MenuItem>
                )}
                {onDelete && (
                  <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                )}
              </WbMoreOptions>
            )}
          </ButtonBase>
        ))}
        {loading && (
          <Box style={{ textAlign: 'center', padding: 12 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!loading && hasMore && sessions.length > 0 && (
          <Box className={classes.loadMoreButton}>
            <ButtonBase
              onClick={() => onLoadMore?.()}
              aria-label="Load more conversations"
            >
              <Typography variant="body2" color="primary">
                Load more...
              </Typography>
            </ButtonBase>
          </Box>
        )}
        {!loading && sessions.length === 0 && (
          <WbNoData text="No conversations yet" showIcon={false} />
        )}
      </Box>
    </Box>
  );
};

export const WittyChat: FC<ChatProps> = ({
  setOpen,
  setChatExpanded,
  isMicrophoneActive,
  isAttachContentActive,
  isFeedbackActive,
  onNewSession,
  mobile = false,
  historyOpen = false,
  setHistoryOpen,
  sessions = [],
  sessionsLoading = false,
  loadSessions,
  loadMoreSessions,
  hasMoreSessions = false,
  searchSessions,
  sessionSearchQuery = '',
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  activeSessionId,
  ...props
}) => {
  const classes = useStyles();

  const handleToggleHistory = setHistoryOpen
    ? () => {
        setHistoryOpen(prev => {
          if (!prev && loadSessions) loadSessions();
          return !prev;
        });
      }
    : undefined;

  const handleCloseHistory = setHistoryOpen
    ? () => setHistoryOpen(false)
    : undefined;

  return (
    <Box className={classes.rootWithHistory}>
      <Box className={classes.chatPanel}>
        <ChatContextProvider {...props}>
          <WittyHeader
            mobile={mobile}
            setOpen={setOpen}
            setChatExpanded={setChatExpanded}
            onNewSession={onNewSession}
            onToggleHistory={handleToggleHistory}
          />
          <WittyContent
            isMicrophoneActive={isMicrophoneActive}
            isAttachContentActive={isAttachContentActive}
            isFeedbackActive={isFeedbackActive}
          />
        </ChatContextProvider>
      </Box>
      {historyOpen && (
        <HistorySidebar
          sessions={sessions}
          loading={sessionsLoading}
          onSelect={onSelectSession}
          onRename={onRenameSession}
          onDelete={onDeleteSession}
          onLoadMore={loadMoreSessions}
          hasMore={hasMoreSessions}
          onClose={handleCloseHistory}
          activeSessionId={activeSessionId}
          searchQuery={sessionSearchQuery}
          onSearch={searchSessions}
        />
      )}
    </Box>
  );
};
